import React from "react";
import "./DigitalTimer.css";

interface IProps {
  editable?: boolean;
  duration: number;
  onChangeDuration: (timestamp: number) => void;
}

const MINUTE_IN_MILLISECONDS = 1000 * 60;
const SECOND_IN_MILLISECONDS = 1000;

function getStringWithPaddingZeros(number: number) {
  return `${number}`.padStart(2, "0");
}

function getLastTwoDigits(minutes: number): number {
  return Math.abs(minutes) % 100;
}

function getNormalizedDuration({ minutes, seconds }: { minutes: number; seconds: number }) {
  return (
    (getLastTwoDigits(minutes) % 60) * MINUTE_IN_MILLISECONDS +
    (getLastTwoDigits(seconds) % 60) * SECOND_IN_MILLISECONDS
  );
}

export function DigitalTimer(props: IProps) {
  const { editable = false, duration, onChangeDuration } = props;
  const secondsInputRef = React.useRef<HTMLInputElement | null>(null);
  const minutesInputRef = React.useRef<HTMLInputElement | null>(null);

  const date = new Date(duration);
  const seconds = date.getSeconds();
  const minutes = date.getMinutes();
  const minutesString = getStringWithPaddingZeros(minutes);
  const secondsString = getStringWithPaddingZeros(seconds);

  function handleChangeSeconds(event: React.ChangeEvent<HTMLInputElement>) {
    const seconds = parseInt(event.target.value, 0.1);
    if (isNaN(seconds)) return;

    const normalizedDuration = getNormalizedDuration({ seconds, minutes });
    onChangeDuration(normalizedDuration);
  }

  function handleChangeMinutes(event: React.ChangeEvent<HTMLInputElement>) {
    const minutes = parseInt(event.target.value, 0.1);
    if (isNaN(minutes)) return;

    const normalizedDuration = getNormalizedDuration({ seconds, minutes });
    onChangeDuration(normalizedDuration);
  }

  function handleSelectMinutesInput() {
    minutesInputRef.current?.select();
  }

  function handleSelectSecondsInput() {
    secondsInputRef.current?.select();
  }

  return (
    <div className="DigitalTimer">
      {editable ? (
        <input
          ref={minutesInputRef}
          size={2}
          type="text"
          value={minutesString}
          aria-label="Minutes"
          className="DigitalTimer__input"
          onChange={handleChangeMinutes}
          onClick={handleSelectMinutesInput}
        />
      ) : (
        <div className="DigitalTimer__label">{minutesString}</div>
      )}
      <div>:</div>
      {editable ? (
        <input
          ref={secondsInputRef}
          size={2}
          type="text"
          value={secondsString}
          aria-label="Seconds"
          className="DigitalTimer__input"
          onChange={handleChangeSeconds}
          onClick={handleSelectSecondsInput}
        />
      ) : (
        <div className="DigitalTimer__label">{secondsString}</div>
      )}
    </div>
  );
}
